import { useEffect, useState } from "react";
import { apiPost, ApiError } from "../../lib/api";

interface UserSnapshot {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}

interface ContactBody {
  name: string;
  email: string;
  subject: string;
  message: string;
}

type Status =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "ok" }
  | { kind: "error"; message: string };

/**
 * Public contact form, ported from the inline JS that lived in
 * `templates/general/contact.html`.
 *
 * Same observable behaviour as before:
 *   - If the visitor is logged in, prefill name + email from /api/user
 *     and lock those fields read-only.
 *   - POST {name, email, subject, message} to /api/contact.
 *   - On success: clear subject + message, surface a green status banner.
 *   - On failure: surface a red status banner.
 *
 * Differences worth noting:
 *   - State lives in React, not in DOM attributes — no more flipping
 *     `readOnly` and toggling CSS classes imperatively.
 *   - Errors are inline next to the form, not a global Toast. The
 *     React island can't see the legacy `Toast` singleton; a banner is
 *     also a better a11y story (the success/error message is in the
 *     submit button's flow rather than off-screen).
 */
export function ContactForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [lockedFromAuth, setLockedFromAuth] = useState({
    name: false,
    email: false,
  });
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  // On mount, try to prefill from the authenticated session.
  // The endpoint is public-friendly: a 401 just means "not logged
  // in" and we leave the fields blank + editable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/user", { credentials: "include" });
        if (!res.ok || cancelled) return;
        const user = (await res.json()) as UserSnapshot;
        if (user.email) {
          setEmail(user.email);
          setLockedFromAuth((p) => ({ ...p, email: true }));
        }
        if (user.first_name && user.last_name) {
          setName(`${user.first_name} ${user.last_name}`);
          setLockedFromAuth((p) => ({ ...p, name: true }));
        }
      } catch {
        // Network/auth error — let the user type their details.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus({ kind: "sending" });
    try {
      await apiPost<unknown, ContactBody>("/api/contact", {
        name,
        email,
        subject,
        message,
      });
      setStatus({ kind: "ok" });
      // Match the existing UX: keep name/email, clear the body.
      setSubject("");
      setMessage("");
    } catch (err) {
      const detail =
        err instanceof ApiError
          ? err.detail
          : "Error sending message. Please try again later.";
      setStatus({ kind: "error", message: detail });
    }
  }

  const sending = status.kind === "sending";
  const inputClass =
    "w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg " +
    "text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
  const lockedInputClass = inputClass + " cursor-not-allowed opacity-75";

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label
            htmlFor="contact-name"
            className="block text-sm font-medium text-gray-600 mb-2"
          >
            Name
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            readOnly={lockedFromAuth.name}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={lockedFromAuth.name ? lockedInputClass : inputClass}
          />
        </div>
        <div>
          <label
            htmlFor="contact-email"
            className="block text-sm font-medium text-gray-600 mb-2"
          >
            Email
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            readOnly={lockedFromAuth.email}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={lockedFromAuth.email ? lockedInputClass : inputClass}
          />
        </div>
      </div>
      <div>
        <label
          htmlFor="contact-subject"
          className="block text-sm font-medium text-gray-600 mb-2"
        >
          Subject
        </label>
        <input
          id="contact-subject"
          name="subject"
          type="text"
          required
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className={inputClass}
        />
      </div>
      <div>
        <label
          htmlFor="contact-message"
          className="block text-sm font-medium text-gray-600 mb-2"
        >
          Message
        </label>
        <textarea
          id="contact-message"
          name="message"
          rows={6}
          required
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className={inputClass}
        />
      </div>

      {status.kind === "ok" && (
        <div
          role="status"
          className="rounded-lg bg-green-50 px-4 py-3 text-sm font-medium text-green-700"
        >
          Message sent successfully! We'll get back to you soon.
        </div>
      )}
      {status.kind === "error" && (
        <div
          role="alert"
          className="rounded-lg bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700"
        >
          {status.message}
        </div>
      )}

      <div>
        <button
          type="submit"
          disabled={sending}
          className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {sending ? "Sending…" : "Send Message"}
        </button>
      </div>
    </form>
  );
}
